import { Button } from "@material-tailwind/react";
import { ButtonProps } from "@material-tailwind/react/components/Button";
import React, { ReactNode } from "react";
import CountCircle from "../CountCirle";

// TODO: bruke union types istedet for enums?

export enum ButtonLayout {
  Horizontal,
  Vertical,
  HorizontalReverse,
}

export enum ButtonVariant {
  Primary,
  Secondary,
  Tertiary,
  PrimaryDarkBg,
  SecondaryDarkBg,
  TertiaryDarkBg,
}

export type ButtonDefaultProps = Omit<ButtonProps, "ref" | "children" | "variant" | "icon"> & {
  id: string | number;
  label?: string;
  icon?: JSX.Element;
  variant?: ButtonVariant;
  children?: ReactNode;
  count?: number;
  leftAdjusted?: boolean;
  active?: boolean;
  lessPadding?: boolean;
};

const ButtonDefault: React.ForwardRefRenderFunction<HTMLButtonElement, ButtonDefaultProps> = (
  {
    id,
    label,
    icon,
    fullWidth = false,
    variant = ButtonVariant.Primary,
    children,
    count,
    leftAdjusted,
    active,
    lessPadding,
    ...rest
  },
  ref,
): JSX.Element => {
  let defaultClassName = "";
  switch (variant) {
    case ButtonVariant.Secondary: {
      defaultClassName += " bg-ldir-blue-300 text-ldir-blue-800 border-[3px] border-ldir-blue-800";
      break;
    }
    case ButtonVariant.Tertiary: {
      defaultClassName += " bg-ldir-gray-600 text-ldir-blue-800 border-[3px] border-ldir-blue-800 ";
      break;
    }
    case ButtonVariant.PrimaryDarkBg: {
      defaultClassName += " bg-ldir-blue-400 text-ldir-blue-900 border-[3px] border-ldir-blue-400";
      break;
    }
    case ButtonVariant.SecondaryDarkBg: {
      defaultClassName +=
        " bg-ldir-blue-400/10 text-ldir-blue-400 border-[3px] border-ldir-blue-400";
      break;
    }
    case ButtonVariant.TertiaryDarkBg: {
      defaultClassName += "bg-transparent text-ldir-blue-400 border-[3px] border-ldir-blue-400";
      break;
    }
    default: {
      // Applies to ButtonVarian.Primary and other unhandled variants.
      defaultClassName +=
        " bg-ldir-blue-800 text-ldir-blue-100 border-[3px] border-ldir-blue-800 focus-visible:bg-ldir-blue-800";
      break;
    }
  }
  return (
    <Button
      ref={ref}
      data-testid={id}
      name={label}
      aria-label={label}
      aria-labelledby={label}
      fullWidth={fullWidth}
      {...rest}
      className={`
        normal-case flex items-center gap-2 min-w-24 py-4 w-full rounded-full shadow-none hover:shadow-none 
        focus-visible:opacity-100! active:opacity-100! focus:opacity-100! hover:opacity-100!
        focus-visible:outline-[3px] focus-visible:outline-ldir-blue-600 focus-visible:border-ldir-blue-600
        active:bg-[#81C5D5] active:border-[#81C5D5] active:text-[#01323D] 
        ${lessPadding ? "px-2 md:px-6" : "px-6"} 
        ${leftAdjusted ? "justify-start pl-[calc(50%-100px)]" : "justify-center"} 
        ${defaultClassName} 
        ${rest.className} 
        ${active ? "bg-[#81C5D5] border-[#81C5D5] text-[#01323D]" : ""} `}
    >
      {icon}
      {children ?? <span className="text-xl font-normal">{label}</span>}
      {count != undefined && (
        <CountCircle
          count={count}
          color={variant === ButtonVariant.Primary ? "light" : undefined}
        />
      )}
    </Button>
  );
};

export default React.forwardRef(ButtonDefault);
